import { useReducer } from "react";
import {
  makeMultiLegDraft,
  type MultiLegDraft,
  type MultiLegDraftLeg,
  type MultiLegStructure,
  type StrategyEntryBasisType,
} from "../lib/multiLegPosition";
import type { WatchCanonicalListingIdentity } from "../lib/watchListingIdentity";
import type {
  MovingAverageDirection,
  MovingAverageTriggerMode,
  MovingAverageType,
  MovingAverageWindow,
  MovingAverageWindowPair,
} from "../lib/watchSignalReleaseRegistry";
import type {
  PositionExitDteOperator,
  PositionExitLogic,
  PositionExitShortStrikeSide,
} from "../lib/watchPositionExitDraft";
import type { WatchOptionEvidenceFormDraft } from "../lib/watchOptionEvidenceDraft";

/** The three explicit customer jobs supported by Watch authoring. */
export type WatchAuthoringGoal = "position" | "price_target" | "structure_entry";
/** Exact immutable catalog identity chosen by the customer. */
export interface WatchMarketSignalSelection {
  /** Stable public signal identifier. */
  signalId: string;
  /** Exact reviewed definition version used for preview. */
  definitionVersion: number;
}

/** Exact immutable recipe identity explicitly chosen by the customer. */
export interface WatchMarketRecipeSelection {
  /** Stable reviewed recipe identifier. */
  recipeId: string;
  /** Exact reviewed definition version resolved by the server. */
  definitionVersion: number;
}

/** Explicit customer choices for the progressive Moving averages concept. */
export type WatchMovingAverageDraft =
  | {
    comparison: "PRICE_TO_AVERAGE";
    averageType: MovingAverageType | null;
    window: MovingAverageWindow | null;
    windowPair: null;
    triggerMode: MovingAverageTriggerMode | null;
    direction: MovingAverageDirection | null;
  }
  | {
    comparison: "AVERAGE_CROSSOVER";
    averageType: MovingAverageType | null;
    window: null;
    windowPair: MovingAverageWindowPair | null;
    triggerMode: "CROSSING";
    direction: MovingAverageDirection | null;
  };

/** Fixed, versioned Momentum calculation chosen through progressive disclosure. */
export type WatchMomentumDraft =
  | {
    indicator: "RSI";
    triggerMode: "STATE" | "CROSSING" | null;
    direction: "ABOVE" | "BELOW" | null;
    level: string;
  }
  | {
    indicator: "MACD";
    comparison: "SIGNAL_LINE" | "ZERO" | null;
    direction: "ABOVE" | "BELOW" | null;
  }
  | {
    indicator: "ROC";
    triggerMode: "STATE" | "CROSSING" | null;
    direction: "ABOVE" | "BELOW" | null;
    levelPercent: string;
  };

/** Customer-editable authoring facts and their monotonic mutation revision. */
export interface WatchAuthoringState {
  /** Explicit trade-stage job; null means the customer has not chosen. */
  goal: WatchAuthoringGoal | null;
  /** Options position or candidate facts. */
  draft: MultiLegDraft;
  /** True only after the customer chooses a strategy. */
  structureChosen: boolean;
  /** Market ticker input. */
  ticker: string;
  /** Ticker confirmed through the shared authenticated lookup. */
  confirmedTicker: string;
  /** Canonical active U.S. listing captured with the confirmed market ticker. */
  confirmedListingIdentity: WatchCanonicalListingIdentity | null;
  /** Explicit catalog selection; never defaulted. */
  marketSignal: WatchMarketSignalSelection | null;
  /** Explicit immutable recipe selection; mutually exclusive with an individual signal. */
  marketRecipe: WatchMarketRecipeSelection | null;
  /** Canonical server-bounded decimal values for the selected recipe. */
  marketRecipeParameters: Readonly<Record<string, string>>;
  /** Optional option-evidence clause; absent until explicitly authored. */
  optionEvidenceDraft: WatchOptionEvidenceFormDraft | null;
  /** Progressive Moving averages choices; null until the customer chooses that concept. */
  movingAverageDraft: WatchMovingAverageDraft | null;
  /** Progressive Momentum choices; null until the customer chooses an indicator. */
  momentumDraft: WatchMomentumDraft | null;
  /** Explicit scalar comparison; never defaulted. */
  targetOperator: "gte" | "lte" | null;
  /** Canonical decimal text sent to the server without float coercion. */
  targetThreshold: string;
  /** Whole-position profit target. */
  profitTarget: string;
  /** Preserved deterministic trigger combination semantics. */
  positionExitLogic: PositionExitLogic;
  /** Whole-position loss limit. */
  lossLimit: string;
  /** Percentage of finite maximum profit. */
  maxProfitPercent: string;
  /** Percentage of finite maximum loss. */
  maxLossPercent: string;
  /** Whole days remaining until expiration. */
  dteThreshold: string;
  /** Exact or at-most DTE comparison, never defaulted. */
  dteOperator: PositionExitDteOperator | null;
  /** Whether sampled underlying price should be compared with a short strike. */
  shortStrikeBreach: boolean;
  /** Explicit short-strike side when a structure has both puts and calls. */
  shortStrikeSide: PositionExitShortStrikeSide | null;
  /** Net debit or credit entry target. */
  entryTarget: string;
  /** Maximum Watch lifetime in days. */
  duration: number;
  /** Deterministic evaluation interval in minutes. */
  interval: number;
  /** Delivery lifecycle after an eligible alert. */
  alertMode: "one_and_done" | "keep_watching";
  /** Whether evaluation is constrained to regular U.S. market hours. */
  marketHoursOnly: boolean;
  /**
   * JS-1081 (adversarial-review FIX 4): optional idea-ledger back-link,
   * carried from a Desk-idea "+Watch" pre-fill. DETACHED (set to `null`) by
   * the reducer the moment the customer makes an IDENTITY-changing edit
   * (ticker, a leg's strike/option-type/side, expiration, or the overall
   * structure) -- the ledger row it points to describes a SPECIFIC
   * instrument, and an edit that changes WHICH instrument this is makes the
   * back-link a lie. A TRIGGER-only edit (profit target, loss limit, DTE,
   * etc.) or a BASIS/QUANTITY edit (per-share premium, contract count, net
   * debit/credit amount) does NOT change which instrument this is, so those
   * edits KEEP it -- this is a POSITIONAL-IDENTITY relationship (the
   * held contract), not a relationship to any particular trigger
   * configuration. See ``WatchCreateWizard.tsx``'s own ruling comment where
   * this state field is read at submission time for the full doctrine.
   */
  provenance: { kind: "TRADE_IDEA_LEDGER"; ledgerId: string } | null;
  /**
   * JS-1081 round 3 (adversarial-review FIX 1): the SUBSET of
   * {profitTarget, lossLimit, maxProfitPercent, dteThreshold} still owned by
   * the LIVE relative-exit-rule recompute (see WatchCreateWizard.tsx's
   * basis/quantity-keyed effect). A field starts in this set whenever the
   * effect writes it; the MOMENT the customer manually edits that field
   * (dispatching its own `*.changed` action directly, e.g. by typing in the
   * trigger step), it is removed here and the reducer never again lets the
   * effect overwrite it -- the other fields keep auto-deriving
   * independently. `null` when this Watch was never pre-filled from a
   * relative exit rule at all (a purely manual authoring flow), so the
   * effect never engages and this field is simply inert.
   */
  autoManagedTriggerFields: ReadonlySet<
    "profitTarget" | "lossLimit" | "maxProfitPercent" | "dteThreshold"
  > | null;
  /** Monotonic value used to invalidate previews and async responses. */
  revision: number;
}

/** Supported hydration for duplicate and replacement position flows. */
export interface WatchAuthoringHydration {
  /** The duplicate/replace flow is constrained to the position job. */
  goal: "position";
  /** Preserved readable position facts. */
  draft: MultiLegDraft;
  /** Existing whole-position profit target. */
  profitTarget?: string;
  /** Existing versioned trigger logic; new Watches default to OR. */
  positionExitLogic?: PositionExitLogic;
  /** Existing whole-position loss limit. */
  lossLimit?: string;
  maxProfitPercent?: string;
  maxLossPercent?: string;
  dteThreshold?: string;
  dteOperator?: PositionExitDteOperator | null;
  shortStrikeBreach?: boolean;
  shortStrikeSide?: PositionExitShortStrikeSide | null;
  /** Legacy single threshold retained for readable migration. */
  pnlThreshold?: string;
  /** Legacy threshold direction retained for readable migration. */
  pnlDirection?: "profit" | "loss";
  /** Existing maximum duration. */
  duration?: number;
  /** Existing evaluation interval. */
  interval?: number;
  /** Existing alert lifecycle. */
  alertMode?: "one_and_done" | "keep_watching";
  /** Existing market-hours constraint. */
  marketHoursOnly?: boolean;
  /** JS-1081: optional idea-ledger back-link -- see the state field's own doc. */
  provenance?: { kind: "TRADE_IDEA_LEDGER"; ledgerId: string } | null;
}

/** Every domain mutation is discriminated so dependent facts clear atomically. */
export type WatchAuthoringAction =
  | { type: "goal.selected"; goal: WatchAuthoringGoal }
  | { type: "structure.selected"; structure: MultiLegStructure }
  | { type: "draft.changed"; patch: Partial<MultiLegDraft> }
  | { type: "butterfly_center.changed"; strike: string }
  | { type: "leg.changed"; index: number; patch: Partial<MultiLegDraftLeg> }
  | { type: "options_ticker.changed"; ticker: string }
  | { type: "options_ticker.confirmed"; ticker: string }
  | { type: "expiration.selected"; expiry: string }
  | { type: "option_type.changed"; optionType: "call" | "put" }
  | { type: "single_side.changed"; side: "LONG" | "SHORT" }
  | { type: "strategy_basis.changed"; basisType: StrategyEntryBasisType }
  | { type: "shared_quantity.changed"; contracts: string }
  | { type: "option_evidence.set"; draft: WatchOptionEvidenceFormDraft }
  | { type: "option_evidence.update"; patch: Partial<WatchOptionEvidenceFormDraft> }
  | { type: "option_evidence.remove" }
  | { type: "market_signal.selected"; signal: WatchMarketSignalSelection }
  | {
    type: "market_recipe.selected";
    recipe: WatchMarketRecipeSelection | null;
    parameters?: Readonly<Record<string, string>>;
    evaluationIntervalMinutes?: number;
  }
  | { type: "market_recipe.parameter_changed"; parameterId: string; value: string }
  | {
    type: "moving_average.comparison_selected";
    comparison: WatchMovingAverageDraft["comparison"];
  }
  | { type: "moving_average.type_changed"; averageType: MovingAverageType }
  | { type: "moving_average.window_changed"; window: MovingAverageWindow }
  | { type: "moving_average.pair_changed"; windowPair: MovingAverageWindowPair }
  | { type: "moving_average.trigger_mode_changed"; triggerMode: MovingAverageTriggerMode }
  | { type: "moving_average.direction_changed"; direction: MovingAverageDirection }
  | { type: "momentum.indicator_selected"; indicator: WatchMomentumDraft["indicator"] }
  | { type: "momentum.trigger_mode_changed"; triggerMode: "STATE" | "CROSSING" }
  | { type: "momentum.comparison_changed"; comparison: "SIGNAL_LINE" | "ZERO" }
  | { type: "momentum.direction_changed"; direction: "ABOVE" | "BELOW" }
  | { type: "momentum.level_changed"; value: string }
  | { type: "market_ticker.changed"; ticker: string }
  | {
    type: "market_ticker.confirmed";
    ticker: string;
    listingIdentity: WatchCanonicalListingIdentity | null;
  }
  | { type: "target_operator.changed"; operator: "gte" | "lte" }
  | { type: "target_threshold.changed"; threshold: string }
  | { type: "profit_target.changed"; value: string }
  | { type: "loss_limit.changed"; value: string }
  | { type: "max_profit_percent.changed"; value: string }
  | { type: "max_loss_percent.changed"; value: string }
  | { type: "dte_threshold.changed"; value: string }
  | { type: "dte_operator.changed"; operator: PositionExitDteOperator | null }
  // JS-1081 round 3 (adversarial-review FIX 1): per-field trigger ownership.
  | {
    /**
     * Fired ONCE, when the wizard first detects a `relativeExitRule`
     * pre-fill, to mark which trigger fields start under LIVE auto-derive
     * ownership (see `autoManagedTriggerFields`'s own doc).
     */
    type: "position_exit_fields.auto_manage_started";
    fields: ReadonlyArray<"profitTarget" | "lossLimit" | "maxProfitPercent" | "dteThreshold">;
  }
  | {
    /**
     * Fired by the wizard's basis/quantity-keyed recompute effect on every
     * basis/quantity change. Writes ONLY the fields still present in
     * `autoManagedTriggerFields` (a field the customer already detached by
     * manually editing it is left untouched) -- never routes through the
     * customer-facing `profit_target.changed`/etc. actions, which would
     * incorrectly re-mark a field as customer-owned. When `mapped` is
     * `null` (the rule's polarity no longer matches the current signed
     * basis, e.g. a debit/credit flip), every STILL-auto-managed field is
     * CLEARED to `""` (dteOperator to `null`) rather than left stale --
     * an auto field must never keep presenting a submittable number that
     * no longer corresponds to any real computation.
     */
    type: "position_exit_fields.auto_derived";
    mapped: {
      profitTarget?: string;
      lossLimit?: string;
      maxProfitPercent?: string;
      dteThreshold?: string;
      dteOperator?: PositionExitDteOperator;
    } | null;
  }
  | { type: "short_strike_breach.changed"; enabled: boolean }
  | { type: "short_strike_side.changed"; side: PositionExitShortStrikeSide | null }
  | { type: "entry_target.changed"; value: string }
  | { type: "duration.changed"; duration: number }
  | { type: "interval.changed"; interval: number }
  | { type: "alert_mode.changed"; alertMode: "one_and_done" | "keep_watching" }
  | { type: "market_hours.changed"; marketHoursOnly: boolean };

const DEFAULT_DURATION = 5;
const DEFAULT_INTERVAL = 15;

const COMPLETED_DAILY_VOLATILITY_SIGNAL_IDS = new Set([
  "underlying.atr.completed_daily.14.relative_to_level",
  "underlying.bollinger.percent_b.completed_daily.20_2.relative_to_level",
  "underlying.bollinger.bandwidth.completed_daily.20_2.relative_to_level",
  "underlying.realized_volatility.close_to_close.20d_annualized.relative_to_level",
]);

/** Create a complete authoring state without choosing anything for the user. */
export function createWatchAuthoringState(
  hydration?: WatchAuthoringHydration,
): WatchAuthoringState {
  return {
    goal: hydration?.goal ?? null,
    draft: hydration?.draft ?? makeMultiLegDraft(),
    structureChosen: Boolean(hydration),
    ticker: "",
    confirmedTicker: hydration?.draft.underlying ?? "",
    confirmedListingIdentity: null,
    marketSignal: null,
    marketRecipe: null,
    marketRecipeParameters: {},
    optionEvidenceDraft: null,
    movingAverageDraft: null,
    momentumDraft: null,
    targetOperator: null,
    targetThreshold: "",
    profitTarget: hydration?.profitTarget
      ?? (hydration?.pnlDirection !== "loss" ? hydration?.pnlThreshold : "")
      ?? "",
    positionExitLogic: hydration?.positionExitLogic ?? "OR",
    lossLimit: hydration?.lossLimit
      ?? (hydration?.pnlDirection === "loss" ? hydration?.pnlThreshold : "")
      ?? "",
    maxProfitPercent: hydration?.maxProfitPercent ?? "",
    maxLossPercent: hydration?.maxLossPercent ?? "",
    dteThreshold: hydration?.dteThreshold ?? "",
    dteOperator: hydration?.dteOperator ?? null,
    shortStrikeBreach: hydration?.shortStrikeBreach ?? false,
    shortStrikeSide: hydration?.shortStrikeSide ?? null,
    entryTarget: "",
    duration: hydration?.duration ?? DEFAULT_DURATION,
    interval: hydration?.interval ?? DEFAULT_INTERVAL,
    alertMode: hydration?.alertMode ?? "one_and_done",
    marketHoursOnly: hydration?.marketHoursOnly ?? true,
    provenance: hydration?.provenance ?? null,
    autoManagedTriggerFields: null,
    revision: 0,
  };
}

function changed(state: WatchAuthoringState, patch: Partial<WatchAuthoringState>) {
  return { ...state, ...patch, revision: state.revision + 1 };
}

/**
 * JS-1081 round 3 (adversarial-review FIX 1): remove `field` from the
 * auto-managed set (a customer's manual edit permanently detaches JUST that
 * field from live recompute -- the others keep auto-deriving). Returns the
 * SAME set reference when `field` was never a member or the set is already
 * `null` (a purely manual authoring flow), so an unrelated manual edit never
 * allocates a new Set or triggers an unnecessary re-render of dependents.
 */
function detachAutoManagedField(
  fields: WatchAuthoringState["autoManagedTriggerFields"],
  field: "profitTarget" | "lossLimit" | "maxProfitPercent" | "dteThreshold",
): WatchAuthoringState["autoManagedTriggerFields"] {
  if (!fields || !fields.has(field)) return fields;
  const next = new Set(fields);
  next.delete(field);
  return next;
}

function draftForStructure(previous: MultiLegDraft, structure: MultiLegStructure) {
  const next = makeMultiLegDraft(structure, previous.openedAt);
  return { ...next, underlying: previous.underlying, expiry: previous.expiry };
}

/** Apply one atomic customer mutation and clear every incompatible fact. */
export function watchAuthoringReducer(
  state: WatchAuthoringState,
  action: WatchAuthoringAction,
): WatchAuthoringState {
  switch (action.type) {
    case "goal.selected":
      return changed(state, {
        goal: action.goal,
        draft: makeMultiLegDraft(),
        structureChosen: false,
        ticker: "",
        confirmedTicker: "",
        confirmedListingIdentity: null,
        marketSignal: null,
        marketRecipe: null,
        marketRecipeParameters: {},
        optionEvidenceDraft: null,
        movingAverageDraft: null,
        momentumDraft: null,
        targetOperator: null,
        targetThreshold: "",
        profitTarget: "",
        positionExitLogic: "OR",
        lossLimit: "",
        maxProfitPercent: "",
        maxLossPercent: "",
        dteThreshold: "",
        dteOperator: null,
        shortStrikeBreach: false,
        shortStrikeSide: null,
        entryTarget: "",
        interval: DEFAULT_INTERVAL,
        marketHoursOnly: true,
        // JS-1081 (adversarial-review FIX 4): choosing a different goal
        // entirely discards the whole position identity this provenance
        // pointed at.
        provenance: null,
      });
    case "structure.selected": {
      const draft = draftForStructure(state.draft, action.structure);
      if (state.goal === "position" && action.structure === "single") {
        draft.strategyEntryBasisType = "NET_DEBIT";
      }
      return changed(state, {
        draft,
        structureChosen: true,
        optionEvidenceDraft: null,
        maxProfitPercent: "",
        maxLossPercent: "",
        shortStrikeBreach: false,
        shortStrikeSide: null,
        // JS-1081 (adversarial-review FIX 4): the overall structure is
        // IDENTITY -- a different topology is a different position entirely.
        provenance: null,
      });
    }
    case "draft.changed": {
      // JS-1081 (adversarial-review FIX 4): this generic patch action is
      // also how the wizard's OWN "Expiration" field (identity) and
      // "Strategy entry basis" fields (basis, keep) both flow through --
      // only `expiry`/`underlying` in the patch are identity here (`legs`
      // is never patched THIS way; see "leg.changed" above for that).
      const patchTouchesIdentity = "expiry" in action.patch || "underlying" in action.patch;
      return changed(state, {
        draft: { ...state.draft, ...action.patch },
        optionEvidenceDraft: null,
        ...(patchTouchesIdentity ? { provenance: null } : {}),
      });
    }
    case "butterfly_center.changed":
      if (state.draft.structure !== "iron_butterfly") return state;
      return changed(state, {
        draft: { ...state.draft, legs: state.draft.legs.map((leg, index) => index === 1 || index === 2 ? { ...leg, strike: action.strike } : leg) },
        optionEvidenceDraft: null,
        provenance: null,
      });
    case "leg.changed": {
      // JS-1081 (adversarial-review FIX 4): strike/optionType/side are
      // IDENTITY facts about the leg (which contract this is); contracts
      // (quantity) and entryPremiumPerShare (basis) are NOT -- they describe
      // how much of, and what was paid for, the SAME contract. Only detach
      // provenance when the patch actually touches an identity field.
      const patchTouchesIdentity = "strike" in action.patch
        || "optionType" in action.patch
        || "side" in action.patch;
      return changed(state, {
        draft: {
          ...state.draft,
          legs: state.draft.legs.map((leg, index) => (
            index === action.index ? { ...leg, ...action.patch } : leg
          )),
        },
        optionEvidenceDraft: null,
        ...(patchTouchesIdentity ? { provenance: null } : {}),
      });
    }
    case "options_ticker.changed": {
      const ticker = action.ticker.trimStart().toUpperCase();
      return changed(state, {
        confirmedTicker: ticker === state.confirmedTicker ? state.confirmedTicker : "",
        draft: {
          ...state.draft,
          underlying: ticker,
          expiry: "",
          legs: state.draft.legs.map((leg) => ({ ...leg, strike: "" })),
        },
        optionEvidenceDraft: null,
        // JS-1081 (adversarial-review FIX 4): the ticker is the most
        // fundamental identity fact -- always detach.
        provenance: null,
      });
    }
    case "options_ticker.confirmed": {
      const ticker = action.ticker.trim().toUpperCase();
      return changed(state, {
        confirmedTicker: ticker,
        draft: { ...state.draft, underlying: ticker },
        optionEvidenceDraft: null,
        // A CONFIRMED ticker that differs from what the draft already had
        // is still an identity change (the customer picked a different
        // underlying from search results after an initial edit); comparing
        // against the CURRENT draft.underlying (not confirmedTicker, which
        // may already be stale) catches a genuine change while a no-op
        // re-confirmation of the SAME ticker never touches provenance.
        ...(ticker !== state.draft.underlying ? { provenance: null } : {}),
      });
    }
    case "expiration.selected":
      return changed(state, {
        draft: {
          ...state.draft,
          expiry: action.expiry,
          legs: state.draft.legs.map((leg) => ({ ...leg, strike: "" })),
        },
        optionEvidenceDraft: null,
        // JS-1081 (adversarial-review FIX 4): expiry is identity.
        provenance: null,
      });
    case "option_type.changed":
      return changed(state, {
        draft: {
          ...state.draft,
          legs: state.draft.legs.map((leg) => ({ ...leg, optionType: action.optionType })),
        },
        optionEvidenceDraft: null,
        maxProfitPercent: "",
        maxLossPercent: "",
        shortStrikeBreach: false,
        shortStrikeSide: null,
        // JS-1081 (adversarial-review FIX 4): call vs put is identity.
        provenance: null,
      });
    case "single_side.changed":
      return changed(state, {
        draft: {
          ...state.draft,
          strategyEntryBasisType: action.side === "LONG" ? "NET_DEBIT" : "NET_CREDIT",
          legs: state.draft.legs.map((leg, index) => (
            index === 0 ? { ...leg, side: action.side } : leg
          )),
        },
        optionEvidenceDraft: null,
        maxProfitPercent: "",
        maxLossPercent: "",
        shortStrikeBreach: false,
        // JS-1081 (adversarial-review FIX 4): bought vs sold changes which
        // structure this is (a long call and a short call are different
        // positions) -- identity, not basis.
        provenance: null,
        shortStrikeSide: null,
      });
    case "strategy_basis.changed":
      return changed(state, {
        draft: { ...state.draft, strategyEntryBasisType: action.basisType },
      });
    case "shared_quantity.changed":
      return changed(state, {
        draft: {
          ...state.draft,
          legs: state.draft.legs.map((leg) => ({ ...leg, contracts: action.contracts })),
        },
        optionEvidenceDraft: null,
      });
    case "option_evidence.set":
      return changed(state, { optionEvidenceDraft: action.draft });
    case "option_evidence.update":
      if (!state.optionEvidenceDraft) return state;
      return changed(state, {
        optionEvidenceDraft: { ...state.optionEvidenceDraft, ...action.patch },
      });
    case "option_evidence.remove":
      return changed(state, { optionEvidenceDraft: null });
    case "market_signal.selected":
      if (state.marketSignal?.signalId === action.signal.signalId
        && state.marketSignal.definitionVersion === action.signal.definitionVersion
        && state.marketRecipe === null
        && state.movingAverageDraft === null
        && state.momentumDraft === null) {
        return state;
      }
      // The confirmed subject stays put across condition changes: the ticker
      // and its canonical listing identity describe the listing, not the
      // signal, so a ticker-first flow never discards the customer's subject.
      return changed(state, {
        marketSignal: action.signal,
        marketRecipe: null,
        marketRecipeParameters: {},
        movingAverageDraft: null,
        momentumDraft: null,
        targetOperator: null,
        targetThreshold: "",
        interval: COMPLETED_DAILY_VOLATILITY_SIGNAL_IDS.has(action.signal.signalId)
          ? 1440
          : DEFAULT_INTERVAL,
        marketHoursOnly: true,
      });
    case "market_recipe.selected":
      if ((action.recipe === null && state.marketRecipe === null)
        || (action.recipe !== null
          && state.marketRecipe?.recipeId === action.recipe.recipeId
          && state.marketRecipe.definitionVersion === action.recipe.definitionVersion
          && state.marketSignal === null
          && state.movingAverageDraft === null
          && state.momentumDraft === null)) {
        return state;
      }
      return changed(state, {
        marketRecipe: action.recipe,
        marketRecipeParameters: action.recipe ? { ...(action.parameters ?? {}) } : {},
        marketSignal: null,
        movingAverageDraft: null,
        momentumDraft: null,
        targetOperator: null,
        targetThreshold: "",
        interval: action.recipe ? action.evaluationIntervalMinutes ?? 1440 : DEFAULT_INTERVAL,
        marketHoursOnly: true,
      });
    case "market_recipe.parameter_changed":
      if (!state.marketRecipe
        || state.marketRecipeParameters[action.parameterId] === action.value) return state;
      return changed(state, {
        marketRecipeParameters: {
          ...state.marketRecipeParameters,
          [action.parameterId]: action.value,
        },
      });
    case "moving_average.comparison_selected": {
      if (state.movingAverageDraft?.comparison === action.comparison) return state;
      const movingAverageDraft: WatchMovingAverageDraft = action.comparison === "PRICE_TO_AVERAGE"
        ? {
          comparison: "PRICE_TO_AVERAGE",
          averageType: null,
          window: null,
          windowPair: null,
          triggerMode: null,
          direction: null,
        }
        : {
          comparison: "AVERAGE_CROSSOVER",
          averageType: null,
          window: null,
          windowPair: null,
          triggerMode: "CROSSING",
          direction: null,
        };
      return changed(state, {
        marketSignal: null,
        marketRecipe: null,
        marketRecipeParameters: {},
        movingAverageDraft,
        momentumDraft: null,
        targetOperator: null,
        targetThreshold: "",
        interval: 1440,
        marketHoursOnly: true,
      });
    }
    case "moving_average.type_changed": {
      if (!state.movingAverageDraft
        || state.movingAverageDraft.averageType === action.averageType) return state;
      return changed(state, {
        movingAverageDraft: state.movingAverageDraft.comparison === "PRICE_TO_AVERAGE"
          ? {
            ...state.movingAverageDraft,
            averageType: action.averageType,
            window: null,
            triggerMode: null,
            direction: null,
          }
          : {
            ...state.movingAverageDraft,
            averageType: action.averageType,
            windowPair: null,
            direction: null,
          },
      });
    }
    case "moving_average.window_changed":
      if (state.movingAverageDraft?.comparison !== "PRICE_TO_AVERAGE"
        || state.movingAverageDraft.window === action.window) return state;
      return changed(state, {
        movingAverageDraft: {
          ...state.movingAverageDraft,
          window: action.window,
          triggerMode: null,
          direction: null,
        },
      });
    case "moving_average.pair_changed":
      if (state.movingAverageDraft?.comparison !== "AVERAGE_CROSSOVER"
        || state.movingAverageDraft.windowPair === action.windowPair) return state;
      return changed(state, {
        movingAverageDraft: {
          ...state.movingAverageDraft,
          windowPair: action.windowPair,
          direction: null,
        },
      });
    case "moving_average.trigger_mode_changed":
      if (state.movingAverageDraft?.comparison !== "PRICE_TO_AVERAGE"
        || state.movingAverageDraft.triggerMode === action.triggerMode) return state;
      return changed(state, {
        movingAverageDraft: {
          ...state.movingAverageDraft,
          triggerMode: action.triggerMode,
          direction: null,
        },
      });
    case "moving_average.direction_changed":
      if (!state.movingAverageDraft
        || state.movingAverageDraft.direction === action.direction) return state;
      return changed(state, {
        movingAverageDraft: {
          ...state.movingAverageDraft,
          direction: action.direction,
        },
      });
    case "momentum.indicator_selected": {
      if (state.momentumDraft?.indicator === action.indicator) return state;
      const momentumDraft: WatchMomentumDraft = action.indicator === "RSI"
        ? { indicator: "RSI", triggerMode: null, direction: null, level: "" }
        : action.indicator === "MACD"
          ? { indicator: "MACD", comparison: null, direction: null }
          : { indicator: "ROC", triggerMode: null, direction: null, levelPercent: "" };
      return changed(state, {
        marketSignal: null,
        marketRecipe: null,
        marketRecipeParameters: {},
        movingAverageDraft: null,
        momentumDraft,
        targetOperator: null,
        targetThreshold: "",
        interval: 1440,
        marketHoursOnly: true,
      });
    }
    case "momentum.trigger_mode_changed":
      if (!state.momentumDraft || state.momentumDraft.indicator === "MACD"
        || state.momentumDraft.triggerMode === action.triggerMode) return state;
      return changed(state, {
        momentumDraft: {
          ...state.momentumDraft,
          triggerMode: action.triggerMode,
          direction: null,
        },
      });
    case "momentum.comparison_changed":
      if (state.momentumDraft?.indicator !== "MACD"
        || state.momentumDraft.comparison === action.comparison) return state;
      return changed(state, {
        momentumDraft: {
          ...state.momentumDraft,
          comparison: action.comparison,
          direction: null,
        },
      });
    case "momentum.direction_changed":
      if (!state.momentumDraft || state.momentumDraft.direction === action.direction) return state;
      return changed(state, {
        momentumDraft: { ...state.momentumDraft, direction: action.direction },
      });
    case "momentum.level_changed":
      if (!state.momentumDraft || state.momentumDraft.indicator === "MACD") return state;
      return changed(state, {
        momentumDraft: state.momentumDraft.indicator === "RSI"
          ? { ...state.momentumDraft, level: action.value }
          : { ...state.momentumDraft, levelPercent: action.value },
      });
    case "market_ticker.changed": {
      const ticker = action.ticker.trimStart().toUpperCase();
      return changed(state, {
        ticker,
        confirmedTicker: ticker === state.confirmedTicker ? state.confirmedTicker : "",
        confirmedListingIdentity: ticker === state.confirmedTicker
          ? state.confirmedListingIdentity
          : null,
      });
    }
    case "market_ticker.confirmed": {
      const ticker = action.ticker.trim().toUpperCase();
      return changed(state, {
        ticker,
        confirmedTicker: ticker,
        confirmedListingIdentity: action.listingIdentity,
      });
    }
    case "target_operator.changed":
      return changed(state, { targetOperator: action.operator });
    case "target_threshold.changed":
      return changed(state, { targetThreshold: action.threshold });
    case "profit_target.changed":
      return changed(state, {
        profitTarget: action.value,
        autoManagedTriggerFields: detachAutoManagedField(state.autoManagedTriggerFields, "profitTarget"),
      });
    case "loss_limit.changed":
      return changed(state, {
        lossLimit: action.value,
        autoManagedTriggerFields: detachAutoManagedField(state.autoManagedTriggerFields, "lossLimit"),
      });
    case "max_profit_percent.changed":
      return changed(state, {
        maxProfitPercent: action.value,
        autoManagedTriggerFields: detachAutoManagedField(state.autoManagedTriggerFields, "maxProfitPercent"),
      });
    case "max_loss_percent.changed":
      return changed(state, { maxLossPercent: action.value });
    case "dte_threshold.changed":
      return changed(state, {
        dteThreshold: action.value,
        autoManagedTriggerFields: detachAutoManagedField(state.autoManagedTriggerFields, "dteThreshold"),
      });
    case "dte_operator.changed":
      return changed(state, {
        dteOperator: action.operator,
        // The DTE threshold and its match operator are ONE trigger fact
        // together -- editing either manually detaches the pair.
        autoManagedTriggerFields: detachAutoManagedField(state.autoManagedTriggerFields, "dteThreshold"),
      });
    case "position_exit_fields.auto_manage_started":
      return changed(state, { autoManagedTriggerFields: new Set(action.fields) });
    case "position_exit_fields.auto_derived": {
      const owned = state.autoManagedTriggerFields;
      if (!owned || owned.size === 0) return state;
      const { mapped } = action;
      if (mapped === null) {
        // JS-1081 round 3 (adversarial-review FIX 1): the rule's polarity no
        // longer matches the current signed basis (e.g. a debit/credit
        // flip) -- clear every STILL-auto-managed field rather than leaving
        // a stale, submittable number behind. The wizard's own empty-field
        // validation then honestly surfaces "choose at least one alert" if
        // nothing else is set.
        const patch: Partial<WatchAuthoringState> = {};
        if (owned.has("profitTarget")) patch.profitTarget = "";
        if (owned.has("lossLimit")) patch.lossLimit = "";
        if (owned.has("maxProfitPercent")) patch.maxProfitPercent = "";
        if (owned.has("dteThreshold")) {
          patch.dteThreshold = "";
          patch.dteOperator = null;
        }
        return changed(state, patch);
      }
      const patch: Partial<WatchAuthoringState> = {};
      if (owned.has("profitTarget") && mapped.profitTarget !== undefined) {
        patch.profitTarget = mapped.profitTarget;
      }
      if (owned.has("lossLimit") && mapped.lossLimit !== undefined) {
        patch.lossLimit = mapped.lossLimit;
      }
      if (owned.has("maxProfitPercent") && mapped.maxProfitPercent !== undefined) {
        patch.maxProfitPercent = mapped.maxProfitPercent;
      }
      if (owned.has("dteThreshold") && mapped.dteThreshold !== undefined) {
        patch.dteThreshold = mapped.dteThreshold;
        if (mapped.dteOperator) patch.dteOperator = mapped.dteOperator;
      }
      if (Object.keys(patch).length === 0) return state;
      return changed(state, patch);
    }
    case "short_strike_breach.changed":
      return changed(state, {
        shortStrikeBreach: action.enabled,
        shortStrikeSide: action.enabled ? state.shortStrikeSide : null,
      });
    case "short_strike_side.changed":
      return changed(state, { shortStrikeSide: action.side });
    case "entry_target.changed":
      return changed(state, { entryTarget: action.value });
    case "duration.changed":
      return changed(state, { duration: action.duration });
    case "interval.changed":
      return changed(state, { interval: action.interval });
    case "alert_mode.changed":
      return changed(state, { alertMode: action.alertMode });
    case "market_hours.changed":
      return changed(state, { marketHoursOnly: action.marketHoursOnly });
  }
}

/** Own the customer-editable Watch draft behind one discriminated reducer. */
export function useWatchAuthoringState(hydration?: WatchAuthoringHydration) {
  return useReducer(watchAuthoringReducer, hydration, createWatchAuthoringState);
}
